import * as React from 'react';
import './style/index.scss';
import { Alert, FormGroup, Select, TextArea } from 'ps-design';
import { PriorityLevel } from './types/Enum';
import { priorityLevelOptions, supportedtFileExtensions } from './types/Constants';
import { ContactUsFormProps } from './types/ContactUsFormProps';
import { ContactFormData } from './types/ContactFormData';
import { PriorityLevelMessages } from './types/Enum';
import { ArticleSearch } from '../ArticleSearch';

interface ContactUsFormContentProps extends ContactUsFormProps {
    onSubmissionCallBack(ticketId: Nullable<number>, isSubmissionSuccess: boolean): void;
}

interface ContactUsFormContentState {
    productName: Nullable<string>;
    description: Nullable<string>;
    subject: Nullable<string>;
    priority: Nullable<string>;
    pageSource: Nullable<string>;
    attachments?: Nullable<File[]>;
    isfileUploadError: boolean;
    isSubmissionError: boolean;
    fileUploadErrorMessage: Nullable<string>;
    fileSizeLimitInBytes: number;
    isSubmissionSuccess: Nullable<boolean>;
    sourceUrl: Nullable<string>;
    isSending: boolean;
}

export class ContactUsFormContent extends React.Component<ContactUsFormContentProps, ContactUsFormContentState> {
    constructor(props: ContactUsFormContentProps) {
        super(props);
        this.handleClickCancel = this.handleClickCancel.bind(this);
        this.handleClickSubmit = this.handleClickSubmit.bind(this);
        this.handleSaveAttachment = this.handleSaveAttachment.bind(this);
        this.handleSetSubject = this.handleSetSubject.bind(this);
        this.handleSetDescription = this.handleSetDescription.bind(this);
        this.handlePriorityLevelChange = this.handlePriorityLevelChange.bind(this);
        this.renderEachAttachment = this.renderEachAttachment.bind(this);
        this.handleRemoveAttachment = this.handleRemoveAttachment.bind(this);
        this.handleErrorBannerClose = this.handleErrorBannerClose.bind(this);

        this.state = {
            productName: this.props.productName,
            description: '',
            subject: '',
            priority: PriorityLevel.Minor,
            pageSource: this.props.pageSource,
            attachments: [],
            isfileUploadError: false,
            isSubmissionError: false,
            fileUploadErrorMessage: '',
            fileSizeLimitInBytes: 25000000,
            isSubmissionSuccess: null,
            sourceUrl: window.location.href,
            isSending: false
        };
    }

    render(): React.ReactNode {
        const caseDeflectionClassName = this.props.showCaseDeflection ?
            'contact-us-form__content-wrapper--case-deflection' : '';
        return (
            <div className="contact-us-form__content">
                {this.renderHeader()}
                <div className={`contact-us-form__content-wrapper ${caseDeflectionClassName}`}>
                    <div className="contact-us-form__layout">
                        {this.renderErrorMessage()}
                        {this.renderSubject()}
                        {this.renderDescription()}
                        {this.renderPrioritySection()}
                        {this.renderAttachmentSection()}
                        {this.renderButtons()}
                    </div>
                    {this.props.showCaseDeflection && this.renderArticleSearch()}
                </div>
            </div>
        );
    }

    private renderArticleSearch(): React.ReactNode {
        const { articles, initialSearchTerms, isVisible, searchTermCallBack, goToArticle } = this.props;
        return (
            <div className="article-search-container">
                <h3 className="article-search-container__header">
                    Find your answer quickly
                </h3>
                <ArticleSearch
                    articles={articles}
                    initialSearchTerms={initialSearchTerms || ''}
                    isVisible={isVisible as boolean}
                    searchTermCallBack={searchTermCallBack}
                    goToArticle={goToArticle}
                />
            </div>
        );
    }

    private renderHeader(): JSX.Element {
        return (
            <div className="contact-us-form__header">
                <h1>Contact Us</h1>
                <h2>We're here to help!</h2>
            </div>
        );
    }

    private renderSubject(): JSX.Element {
        return (
            <div className="contact-us-form__subject">
                <h3 className="contact-us-form__subject-header">
                    Create a Support Ticket
                </h3>
                <label className="contact-us-form__label contact-us-form__label--required">Subject</label>
                <input
                    className="contact-us-form__subject-input pxl-form-control"
                    value={this.state.subject as string}
                    onChange={this.handleSetSubject}
                    disabled={this.state.isSending}
                />
            </div>
        );
    }

    private renderDescription(): JSX.Element {
        return (
            <div className="contact-us-form__description">
                <label className="contact-us-form__label contact-us-form__label--required">Description</label>
                <FormGroup>
                    <TextArea
                        id="contact-us-form-description"
                        value={this.state.description}
                        onChange={this.handleSetDescription}
                        rows={10}
                        placeholder=""
                        disabled={this.state.isSending}
                    />
                </FormGroup>
            </div>
        );
    }

    private renderPrioritySection(): JSX.Element {
        return (
            <div className="contact-us-form__priority-wrapper">
                <label className="contact-us-form__label">Priority Level</label>
                <div className="contact-us-form__priority-content">
                    {this.renderPriorityDropDown()}
                    {this.renderPriorityMessage()}
                </div>
            </div>
        );
    }

    private renderPriorityDropDown(): JSX.Element {
         return (
            <Select
             id="contact-us-form-priority"
             value={this.state.priority}
             onChange={this.handlePriorityLevelChange}
             options={priorityLevelOptions}
            />
        );
    }

    private renderPriorityMessage(): JSX.Element {
        const { priority } = this.state;
        const message = this.matchPriorityMessages(priority as string);
        return (
            <p className="contact-us-form__priority-message">{message}</p>
        );
    }

    private matchPriorityMessages(priority: string): string {
        switch (priority) {
            case PriorityLevel.Major:
                return PriorityLevelMessages.Major;
            case PriorityLevel.Minor:
                return PriorityLevelMessages.Minor;
            case PriorityLevel.Blocked:
                return PriorityLevelMessages.Blocked;
            default:
                return PriorityLevelMessages.Minor;
        }
    }

    private renderAttachmentSection(): JSX.Element {
        return (
            <div className="contact-us-form__attachment">
                <div className="contact-us-form__attachment-content">
                    <label className="contact-us-form__label">Add File</label>
                    <div className="contact-us-form__file-upload pxl-btn pxl-btn-default">
                        Browse
                        <input type="file" className="contact-us-form__input" onChange={this.handleSaveAttachment} />
                    </div>
                    <p className="contact-us-form__attachment-warning">
                        Please refrain from attaching sensitive company data to this request,
                        you may upload them instead.
                    </p>
                    {this.renderAttachments()}
                </div>
            </div>
        );
    }

    private renderAttachments(): React.ReactNode {
        if (!this.state.attachments?.length) {
            return null;
        }

        return (
            <div className="contact-us-form__attachment-list">
                {this.state.attachments.map(this.renderEachAttachment)}
            </div>
        );
    }

    private renderEachAttachment(file: File, index: number): JSX.Element {
        const onClickHandler = () => {
            this.handleRemoveAttachment(index);
        };

        return(
            <div key={index} className="form-attachment">
                <p className="form-attachment__file-name">{file.name}</p>
                <button
                    className="form-attachment__delete-button"
                    onClick={onClickHandler}
                >
                    <i className="icon-cancel" />
                </button>
            </div>
        );
    }

    private renderButtons(): JSX.Element {
        const buttonText = this.state.isSending ? 'Sending...' : 'Submit';
        // TODO move to psl-btn--primary once lab is on pxl4
        return (
            <div className="contact-us-form__footer">
                <a className="contact-us-form__cancel-button" onClick={this.handleClickCancel}>
                    Cancel
                </a>
                <button
                    onClick={this.handleClickSubmit}
                    className="contact-us-form__submit-button pxl-btn pxl-btn-primary"
                    disabled={this.isSendButtonDisabled()}
                >
                    {buttonText}
                </button>
            </div>
        );
    }

    private renderErrorMessage(): React.ReactNode {
        if (!this.state.isfileUploadError && !this.state.isSubmissionError) {
            return null;
        }

        if (this.state.isfileUploadError) {
            return (
                <Alert
                    message={this.state.fileUploadErrorMessage}
                    level="danger"
                    close={true}
                    onClickClose={this.handleErrorBannerClose}
                />
            );
        }

        return (
            <Alert
                message="Submission failed. Please try again"
                level="danger"
                close={true}
                onClickClose={this.handleErrorBannerClose}
            />
        );
    }

    private handleErrorBannerClose(): void {
        this.setState({isfileUploadError: false, isSubmissionError: false});
    }

    private handleRemoveAttachment(index: number): void {
        const attachments = [...this.state.attachments as Array<any>];
        attachments.splice(index, 1);
        this.setState({ attachments });
    }

    private handleClickCancel(): void {
        this.props.onClose();
    }

    private async handleClickSubmit(): Promise<void> {
        this.setState({ isSending: true });
        const capturedData: ContactFormData = {
            productName: this.state.productName,
            priority: this.state.priority,
            description: this.state.description,
            subject: this.state.subject,
            pageSource: this.props.pageSource,
            attachments: this.state.attachments,
            sourceUrl: this.state.sourceUrl
        };

        try {
            this.handleErrorBannerClose();
            const result = await this.props.onSubmitFn(capturedData);
            if (result?.success) {
                this.props.onSubmissionCallBack((result.ticketId) as number, result.success);
            } else {
            this.setState({isSending: false, isSubmissionError: true});
            }
        } catch (e) {
            this.setState({isSending: false, isSubmissionError: true});
        }
    }

    private handleSaveAttachment(changeEvent: React.ChangeEvent<HTMLInputElement>): void {
        const attachments = [...this.state.attachments as Array<any>];
        if (changeEvent.target.files) {
            const valid = this.validateFile(changeEvent.target.files[0]);
            if (valid) {
                this.clearFileErrorState();
                attachments.push(changeEvent.target.files[0]);
                this.setState({ attachments });
            }
        }
    }

    private handleSetSubject(e: React.ChangeEvent<HTMLInputElement>): void {
        if (e?.target?.value != null) {
            this.setState({ subject: e.target.value});
        }
    }

    private handleSetDescription(_: undefined, value: string): void {
        this.setState({ description: value});
    }

    private handlePriorityLevelChange(_: undefined, value: string): void {
        this.setState({ priority: value});
    }

    private isSendButtonDisabled(): boolean {
        return !(!this.state.isSending && (!!this.state.subject && !!this.state.description));
    }

    private clearFileErrorState(): void {
        this.setState({
            isfileUploadError: false,
            fileUploadErrorMessage: ''
        });
    }

    private validateFileExtension(file: File): boolean {
        const isValid = !!file
                            && supportedtFileExtensions
                                .some(fileExtension => file.name.endsWith(fileExtension));
        if (!isValid) {
            this.setState({
                isfileUploadError: true,
                fileUploadErrorMessage: 'Unsupported File Type. Please upload a supported type.'
            });
        }

        return isValid;
    }

    private validateFileSize(file: File): boolean {
        const isValid = !!file && file.size <= this.state.fileSizeLimitInBytes;
        if (!isValid) {
            this.setState({
                isfileUploadError: true,
                fileUploadErrorMessage: 'Size limit exceeded. Please upload a file of size less than 25Mb.'
            });
        }

        return isValid;
    }

    private validateFile(file: File): boolean {
      return !!file && this.validateFileSize(file) && this.validateFileExtension(file);
    }
}
